import { defineStore } from 'pinia'
import { computed, ref, shallowRef, watch } from 'vue'
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { useDocument } from 'vuefire'
import type { User as FirebaseUser } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { firestore, functions } from '@/fb'
import { debug, warn } from '@/logger'
import type { UserProfile, ColorPreferences, FontPreferences, UserSubscription, SubscriptionStatus, MembershipTier, TierConfig } from '@/models/firestore'
import { MEMBERSHIP_TIERS, CREATOR_USER_IDS, FOUNDING_MEMBER_CUTOFF } from '@/models/firestore'

export interface GiftSubscriptionUser {
  userId: string
  email: string | null
  name: string | null
  reason: string
  giftedBy: string
  expiresAt: string | null
}

export interface GrantGiftSubscriptionInput {
  targetEmail?: string
  targetUserId?: string
  reason: string
  durationDays: number
}

export interface GrantGiftCreditsInput {
  targetEmail?: string
  targetUserId?: string
  reason: string
  credits: 100 | 500
}

export const useUserStore = defineStore('user', () => {
  // Core state - FirebaseUser from auth (not persisted in Firestore)
  const firebaseUser = ref<FirebaseUser | null>(null)

  // VueFire reactive document source - will be null when no user is logged in
  const userDocSource = computed(() =>
    firebaseUser.value
      ? doc(firestore, 'users', firebaseUser.value.uid)
      : null
  )

  // VueFire binding - automatically subscribes/unsubscribes based on source
  const {
    data: userProfile,
    pending: userProfilePending,
    error: userProfileError
  } = useDocument<UserProfile>(userDocSource)

  // Merged user data: Firebase auth data + Firestore profile
  const user = computed<UserProfile | null>(() => {
    if (!firebaseUser.value) return null

    // Get photo URL - try provider data first, then fall back to Firebase Auth photoURL
    let photoURL = firebaseUser.value.photoURL
    if (!photoURL && firebaseUser.value.providerData && firebaseUser.value.providerData.length > 0) {
      photoURL = firebaseUser.value.providerData[0].photoURL
    }

    // Base user from Firebase Auth
    const baseUser: UserProfile = {
      uid: firebaseUser.value.uid,
      name: firebaseUser.value.displayName,
      email: firebaseUser.value.email,
      image_url: photoURL,
      current_pantry: null,
      colors: null,
      pantry_item_form_complexity_default: 'simple',
      pantry_item_optional_data_panel: false,
      show_pantry_item_notes: false,
      show_pantry_item_expiration_date: false,
      show_pantry_item_date_added: false,
      show_pantry_item_price: false,
      show_pantry_item_total_calories: false,
      show_pantry_item_brand: false,
      show_pantry_item_location: false
    }

    // Merge with Firestore profile if available
    if (userProfile.value) {
      return {
        ...baseUser,
        ...userProfile.value
      }
    }

    return baseUser
  })

  // Getters
  const getUser = computed(() => user.value)
  const colors = computed((): ColorPreferences | null => user.value?.colors ?? null)
  const darkMode = computed((): boolean => user.value?.darkMode ?? false)
  const fonts = computed((): FontPreferences | null => user.value?.fonts ?? null)
  const isLoggedIn = computed((): boolean => Boolean(user.value))
  const currentPantry = computed((): string | null => user.value?.current_pantry ?? null)
  const pantryItemOptionalDataPanel = computed((): boolean => user.value?.pantry_item_optional_data_panel ?? false)
  const pantryItemFormComplexityDefault = computed((): 'simple' | 'advanced' | string =>
    user.value?.pantry_item_form_complexity_default ?? 'simple'
  )
  const showPantryItemNotes = computed((): boolean => user.value?.show_pantry_item_notes ?? false)
  const showPantryItemExpirationDate = computed((): boolean => user.value?.show_pantry_item_expiration_date ?? false)
  const showPantryItemDateAdded = computed((): boolean => user.value?.show_pantry_item_date_added ?? false)
  const showPantryItemPrice = computed((): boolean => user.value?.show_pantry_item_price ?? false)
  const showPantryItemTotalCalories = computed((): boolean => user.value?.show_pantry_item_total_calories ?? false)
  const showPantryItemBrand = computed((): boolean => user.value?.show_pantry_item_brand ?? false)
  const showPantryItemLocation = computed((): boolean => user.value?.show_pantry_item_location ?? false)

  // Favorites getters
  const favoriteRecipes = computed((): string[] => user.value?.favorite_recipes ?? [])
  const isFavoriteRecipe = (recipeId: string): boolean => favoriteRecipes.value.includes(recipeId)

  // Subscription getters
  const subscription = computed((): UserSubscription | null => user.value?.subscription ?? null)
  const isPremium = computed((): boolean => {
    const status = subscription.value?.status
    return status === 'active' || status === 'gift'
  })
  const subscriptionStatus = computed((): SubscriptionStatus => subscription.value?.status ?? 'free')
  const isGiftSubscription = computed((): boolean => subscription.value?.status === 'gift')

  // Membership tier getters
  const accountCreationDate = computed((): Date | null => {
    if (!firebaseUser.value?.metadata?.creationTime) return null
    return new Date(firebaseUser.value.metadata.creationTime)
  })

  const membershipTier = computed((): MembershipTier => {
    const uid = user.value?.uid
    if (!uid) return 'free'

    // Check if user is a Creator (hardcoded IDs)
    if (CREATOR_USER_IDS.includes(uid)) {
      return 'creator'
    }

    // Check subscription status
    const status = subscription.value?.status

    // If user has active/gift subscription
    if (status === 'active' || status === 'gift') {
      // Check if they're a Founding Member (account created before cutoff)
      const createdAt = accountCreationDate.value
      if (createdAt && createdAt < FOUNDING_MEMBER_CUTOFF) {
        return 'founding_member'
      }

      // TODO: Check for pro_member subscription plan when implemented
      // if (subscription.value?.plan === 'pro') return 'pro_member'

      return 'member'
    }

    // Check if free user qualifies as Founding Member (early adopter without paid sub)
    // They still get the title but with free tier limits until they subscribe
    // Note: We could make founding_member work differently - for now, need subscription

    return 'free'
  })

  const tierConfig = computed((): TierConfig => {
    return MEMBERSHIP_TIERS[membershipTier.value]
  })

  const tierDisplayName = computed((): string => tierConfig.value.displayName)
  const tierShortName = computed((): string => tierConfig.value.shortName)
  const tierIcon = computed((): string => tierConfig.value.icon)
  const tierColor = computed((): string => tierConfig.value.color)
  const hasFullAccess = computed((): boolean => tierConfig.value.hasFullAccess)

  // Actions
  const setCurrentPantry = async (pantryId: string | null) => {
    debug('setCurrentPantry called', pantryId)
    if (!firebaseUser.value) return

    try {
      const userRef = doc(firestore, 'users', firebaseUser.value.uid)
      await updateDoc(userRef, {
        current_pantry: pantryId
      })
    } catch (error) {
      warn('Could not save current_pantry to Firestore:', error)
    }
  }

  const saveColor = async ({ key, newColor }: { key: string; newColor: string }) => {
    if (!firebaseUser.value) return

    const updatedColors = {
      ...(user.value?.colors ?? {}),
      [key]: newColor
    }

    try {
      const userRef = doc(firestore, 'users', firebaseUser.value.uid)
      await updateDoc(userRef, {
        colors: updatedColors
      })
    } catch (error) {
      warn('saveColor: Error saving color', error)
    }

  }

  const resetColor = async (key: string) => {
    if (!firebaseUser.value) return
    const updatedColors = { ...(user.value?.colors ?? {}) }
    delete updatedColors[key]
    if (key === 'accent') delete updatedColors.tertiary
    if (key === 'tertiary') delete updatedColors.accent

    try {
      const userRef = doc(firestore, 'users', firebaseUser.value.uid)
      await updateDoc(userRef, { colors: updatedColors })
    } catch (error) {
      warn('resetColor: Error resetting color', error)
    }
  }

  const saveDarkMode = async (isDark: boolean) => {
    if (!firebaseUser.value) return

    try {
      const userRef = doc(firestore, 'users', firebaseUser.value.uid)
      await updateDoc(userRef, {
        darkMode: isDark
      })
    } catch (error) {
      warn('saveDarkMode: Error saving dark mode', error)
    }
  }

  const saveFont = async ({ key, fontFamily }: { key: 'headerFont' | 'bodyFont'; fontFamily: string }) => {
    if (!firebaseUser.value) return

    const updatedFonts = {
      ...(user.value?.fonts ?? {}),
      [key]: fontFamily
    }

    try {
      const userRef = doc(firestore, 'users', firebaseUser.value.uid)
      await updateDoc(userRef, {
        fonts: updatedFonts
      })
    } catch (error) {
      warn('saveFont: Error saving font', error)
    }

  }

  const resetFont = async (key: 'headerFont' | 'bodyFont') => {
    if (!firebaseUser.value) return
    const updatedFonts = { ...(user.value?.fonts ?? {}) }
    delete updatedFonts[key]
    try {
      const userRef = doc(firestore, 'users', firebaseUser.value.uid)
      await updateDoc(userRef, { fonts: updatedFonts })
    } catch (error) {
      warn('resetFont: Error resetting font', error)
    }
  }

  const saveFontSize = async (fontSize: number) => {
    if (!firebaseUser.value) return

    const updatedFonts = {
      ...(user.value?.fonts ?? {}),
      fontSize
    }

    try {
      const userRef = doc(firestore, 'users', firebaseUser.value.uid)
      await updateDoc(userRef, {
        fonts: updatedFonts
      })
    } catch (error) {
      warn('saveFontSize: Error saving font size', error)
    }

  }

  const resetFontSize = async () => {
    if (!firebaseUser.value) return
    const updatedFonts = { ...(user.value?.fonts ?? {}) }
    delete updatedFonts.fontSize
    try {
      const userRef = doc(firestore, 'users', firebaseUser.value.uid)
      await updateDoc(userRef, { fonts: updatedFonts })
    } catch (error) {
      warn('resetFontSize: Error resetting font size', error)
    }
  }

  const savePantryItemOptionalDataPanel = async (isVisible: boolean) => {
    if (!firebaseUser.value) return

    try {
      const userRef = doc(firestore, 'users', firebaseUser.value.uid)
      await updateDoc(userRef, {
        pantry_item_optional_data_panel: isVisible
      })
    } catch (error) {
      warn('savePantryItemOptionalDataPanel: Error', error)
    }
  }

  const toggleFavoriteRecipe = async (recipeId: string) => {
    if (!firebaseUser.value) return

    const currentFavorites = user.value?.favorite_recipes ?? []
    const isFavorite = currentFavorites.includes(recipeId)
    const updatedFavorites = isFavorite
      ? currentFavorites.filter(id => id !== recipeId)
      : [...currentFavorites, recipeId]

    try {
      const userRef = doc(firestore, 'users', firebaseUser.value.uid)
      await updateDoc(userRef, {
        favorite_recipes: updatedFavorites
      })
    } catch (error) {
      warn('toggleFavoriteRecipe: Error', error)
    }
  }

  const handleAuthChanged = async (fbUser: FirebaseUser | null): Promise<UserProfile | null> => {
    firebaseUser.value = fbUser

    if (fbUser) {
      // VueFire will automatically start listening to the user document
      // But we also need to ensure the user document exists with basic profile info
      debug('handleAuthChanged: User logged in, ensuring user document exists')

      try {
        const userRef = doc(firestore, 'users', fbUser.uid)
        const userDoc = await getDoc(userRef)

        // Create or update user document with auth info
        const authData: Record<string, unknown> = {
          uid: fbUser.uid
        }

        // Resolve best photo URL from auth (provider data can be more reliable than fbUser.photoURL)
        let resolvedPhotoURL: string | null = fbUser.photoURL
        if (!resolvedPhotoURL && fbUser.providerData && fbUser.providerData.length > 0) {
          resolvedPhotoURL = fbUser.providerData[0]?.photoURL ?? null
        }

        // Only update these fields if they have values from auth
        if (fbUser.displayName) {
          authData.name = fbUser.displayName
        }
        if (fbUser.email) {
          authData.email = fbUser.email
        }
        if (resolvedPhotoURL) {
          authData.image_url = resolvedPhotoURL
        }

        if (!userDoc.exists()) {
          // Create new user document
          debug('handleAuthChanged: Creating new user document')
          await setDoc(userRef, authData)
        } else {
          // Update existing document with any new auth info (merge)
          // Only update if name is missing in the document
          const existingData = userDoc.data()
          if (!existingData?.name && fbUser.displayName) {
            debug('handleAuthChanged: Updating user document with display name')
            await setDoc(userRef, authData, { merge: true })
          }

          // If the document is missing an image_url but we have one from auth, merge it in.
          if (!existingData?.image_url && resolvedPhotoURL) {
            debug('handleAuthChanged: Updating user document with image_url')
            await setDoc(userRef, { image_url: resolvedPhotoURL }, { merge: true })
          }
        }
      } catch (error) {
        warn('handleAuthChanged: Error ensuring user document', error)
      }

      return user.value
    }

    debug('handleAuthChanged: User logged out')
    return null
  }

  const unbindProfile = () => {
    // VueFire handles unbinding automatically when firebaseUser is set to null
    firebaseUser.value = null
  }

  // Subscription actions
  const createCheckoutSession = async (plan: 'monthly' | 'annual'): Promise<{ url: string } | null> => {
    if (!firebaseUser.value) return null

    try {
      const createSession = httpsCallable<
        { plan: string; successUrl: string; cancelUrl: string },
        { sessionId: string; url: string }
      >(functions, 'createCheckoutSession')

      const result = await createSession({
        plan,
        successUrl: `${window.location.origin}/subscription-success`,
        cancelUrl: `${window.location.origin}/pricing?subscription=canceled`
      })

      return { url: result.data.url }
    } catch (error) {
      warn('createCheckoutSession: Error', error)
      throw error
    }
  }

  const createPortalSession = async (): Promise<{ url: string } | null> => {
    if (!firebaseUser.value) return null

    try {
      const createSession = httpsCallable<
        { returnUrl: string },
        { url: string }
      >(functions, 'createPortalSession')

      const result = await createSession({
        returnUrl: `${window.location.origin}/settings`
      })

      return { url: result.data.url }
    } catch (error) {
      warn('createPortalSession: Error', error)
      throw error
    }
  }

  // Purchase credit packs
  const createCreditPackCheckout = async (pack: '100' | '500'): Promise<{ url: string } | null> => {
    if (!firebaseUser.value) return null

    try {
      const createSession = httpsCallable<
        { pack: string; successUrl?: string; cancelUrl?: string },
        { url: string }
      >(functions, 'createCreditPackCheckout')

      const result = await createSession({
        pack,
        successUrl: `${window.location.origin}/?credits=success`,
        cancelUrl: `${window.location.origin}/pricing?credits=canceled`
      })

      return { url: result.data.url }
    } catch (error) {
      warn('createCreditPackCheckout: Error', error)
      throw error
    }
  }

  // Claim free credits (creators only)
  const claimFreeCredits = async (pack: '100' | '500'): Promise<{ success: boolean; credits: number } | null> => {
    if (!firebaseUser.value) return null

    try {
      const claimCredits = httpsCallable<
        { pack: string },
        { success: boolean; credits: number }
      >(functions, 'claimFreeCredits')

      const result = await claimCredits({ pack })
      return result.data
    } catch (error) {
      warn('claimFreeCredits: Error', error)
      throw error
    }
  }

  const grantGiftSubscription = async (
    input: GrantGiftSubscriptionInput
  ): Promise<{ success: boolean; userId: string; expiresAt: string } | null> => {
    if (!firebaseUser.value) return null

    try {
      const grantGift = httpsCallable<
        GrantGiftSubscriptionInput,
        { success: boolean; userId: string; expiresAt: string }
      >(functions, 'grantGiftSubscription')

      const result = await grantGift(input)
      return result.data
    } catch (error) {
      warn('grantGiftSubscription: Error', error)
      throw error
    }
  }

  const grantGiftCredits = async (
    input: GrantGiftCreditsInput
  ): Promise<{ success: boolean; userId: string; credits: number } | null> => {
    if (!firebaseUser.value) return null

    try {
      const grantCredits = httpsCallable<
        GrantGiftCreditsInput,
        { success: boolean; userId: string; credits: number }
      >(functions, 'grantGiftCredits')

      const result = await grantCredits(input)
      return result.data
    } catch (error) {
      warn('grantGiftCredits: Error', error)
      throw error
    }
  }

  const revokeGiftSubscription = async (targetUserId: string): Promise<{ success: boolean } | null> => {
    if (!firebaseUser.value) return null

    try {
      const revokeGift = httpsCallable<
        { targetUserId: string },
        { success: boolean }
      >(functions, 'revokeGiftSubscription')

      const result = await revokeGift({ targetUserId })
      return result.data
    } catch (error) {
      warn('revokeGiftSubscription: Error', error)
      throw error
    }
  }

  const listGiftSubscriptions = async (): Promise<GiftSubscriptionUser[] | null> => {
    if (!firebaseUser.value) return null

    try {
      const listGifts = httpsCallable<object, { users: GiftSubscriptionUser[] }>(
        functions,
        'listGiftSubscriptions'
      )

      const result = await listGifts({})
      return result.data.users
    } catch (error) {
      warn('listGiftSubscriptions: Error', error)
      throw error
    }
  }


  const refreshSubscriptionStatus = async (): Promise<void> => {
    if (!firebaseUser.value) return

    try {
      const getStatus = httpsCallable<object, {
        isPremium: boolean
        status: string
        plan: string | null
        currentPeriodEnd: string | null
        cancelAtPeriodEnd: boolean
        giftReason: string | null
        giftExpiresAt: string | null
      }>(functions, 'getSubscriptionStatus')

      await getStatus({})
      // VueFire will automatically update the user document with latest data
    } catch (error) {
      warn('refreshSubscriptionStatus: Error', error)
    }
  }

  return {
    // State
    user,
    firebaseUser,
    userProfilePending,
    userProfileError,

    // Getters
    getUser,
    colors,
    darkMode,
    fonts,
    saveFontSize,
    resetFontSize,
    isLoggedIn,
    currentPantry,
    pantryItemOptionalDataPanel,
    pantryItemFormComplexityDefault,
    showPantryItemNotes,
    showPantryItemExpirationDate,
    showPantryItemDateAdded,
    showPantryItemPrice,
    showPantryItemTotalCalories,
    showPantryItemBrand,
    showPantryItemLocation,

    // Favorites
    favoriteRecipes,
    isFavoriteRecipe,

    // Subscription getters
    subscription,
    isPremium,
    subscriptionStatus,
    isGiftSubscription,

    // Membership tier getters
    accountCreationDate,
    membershipTier,
    tierConfig,
    tierDisplayName,
    tierShortName,
    tierIcon,
    tierColor,
    hasFullAccess,

    // AI Credits getters
    aiCreditsTotal: computed(() => user.value?.ai_credits_total ?? 0),
    aiCreditsRemaining: computed(() => user.value?.ai_credits_remaining ?? 0),
    aiCreditsRolloverCap: computed(() => user.value?.ai_credits_rollover_cap ?? 0),

    // Actions
    setCurrentPantry,
    saveColor,
    saveDarkMode,
    resetColor,
    saveFont,
    resetFont,
    savePantryItemOptionalDataPanel,
    toggleFavoriteRecipe,
    handleAuthChanged,
    unbindProfile,

    // Subscription actions
    createCheckoutSession,
    createPortalSession,
    refreshSubscriptionStatus,

    // Credits purchase
    createCreditPackCheckout,
    claimFreeCredits,

    // Creator gifting
    grantGiftSubscription,
    grantGiftCredits,
    revokeGiftSubscription,
    listGiftSubscriptions
  }
})
